/**
 * @fileoverview Modal para carregar landpages salvas no MongoDB.
 * Exibe lista com nome, status ativo e ações de selecionar, ativar e deletar.
 *
 * @module LandpageLoadModal
 * @since 1.0.0
 */

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { apiRequestJson } from '../../api/apiService';

/**
 * Modal de carregamento de landpages salvas.
 * Permite selecionar para edição, ativar/desativar e deletar.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Controla visibilidade do modal
 * @param {Function} props.onClose - Callback para fechar
 * @param {Array<Object>} props.landpages - Lista de landpages salvas
 * @param {Function} props.onSelect - Callback ao selecionar landpage para edição
 * @param {Function} props.onRefresh - Callback para recarregar lista após mutação
 * @param {Function} props.onSnackbar - Callback para exibir snackbar no pai
 * @returns {JSX.Element}
 */
export default function LandpageLoadModal({ open, onClose, landpages, onSelect, onRefresh, onSnackbar }) {
  const [loadingId, setLoadingId] = useState(null);

  /**
   * Ativa a landpage selecionada (desativa todas as demais via backend).
   * @async
   * @param {Object} landpage - Landpage a ser ativada
   * @param {React.MouseEvent} e - Evento para evitar propagação ao item da lista
   */
  const handleActivate = async (landpage, e) => {
    e.stopPropagation();
    const id = landpage._id || landpage.id;
    const novaAtivacao = !landpage.active;
    setLoadingId(id);
    try {
      const res = await apiRequestJson('landpage_update', {
        params: { id },
        body: { active: novaAtivacao },
      });
      if (res.success) {
        onSnackbar(
          novaAtivacao
            ? `Landpage "${landpage.name}" ativada.`
            : `Landpage "${landpage.name}" desativada.`,
          'success'
        );
        onRefresh();
      } else {
        throw new Error(res.message);
      }
    } catch (err) {
      onSnackbar(`Erro ao alterar status: ${err.message}`, 'error');
    } finally {
      setLoadingId(null);
    }
  };

  /**
   * Remove a landpage pelo ID.
   * @async
   * @param {Object} landpage - Landpage a ser removida
   * @param {React.MouseEvent} e - Evento para evitar propagação ao item da lista
   */
  const handleDelete = async (landpage, e) => {
    e.stopPropagation();
    const id = landpage._id || landpage.id;
    setLoadingId(id);
    try {
      const res = await apiRequestJson('landpage_delete', { params: { id } });
      if (res.success) {
        onSnackbar(`Landpage "${landpage.name}" removida.`, 'success');
        onRefresh();
      } else {
        throw new Error(res.message);
      }
    } catch (err) {
      onSnackbar(`Erro ao remover: ${err.message}`, 'error');
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        Carregar Landpage
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {landpages.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Alert severity='info'>Nenhuma landpage salva encontrada.</Alert>
          </Box>
        ) : (
          <List disablePadding>
            {/* map: renderiza item para cada landpage salva */}
            {landpages.map((lp, index) => {
              const id = lp._id || lp.id;
              const isLoading = loadingId === id;

              return (
                <Box key={id}>
                  <ListItem
                    disablePadding
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 0.5, mr: 1 }}>
                        <Tooltip title={lp.active ? 'Desativar' : 'Ativar como landpage principal'}>
                          <span>
                            <IconButton
                              size='small'
                              color={lp.active ? 'success' : 'default'}
                              onClick={(e) => handleActivate(lp, e)}
                              disabled={isLoading}
                            >
                              {isLoading ? (
                                <CircularProgress size={16} />
                              ) : (
                                <PowerSettingsNewIcon fontSize='small' />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title='Remover landpage'>
                          <span>
                            <IconButton
                              size='small'
                              color='error'
                              onClick={(e) => handleDelete(lp, e)}
                              disabled={isLoading}
                            >
                              <DeleteIcon fontSize='small' />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemButton onClick={() => onSelect(lp)} sx={{ pr: 12 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant='body1' fontWeight={lp.active ? 'bold' : 'normal'}>
                              {lp.name}
                            </Typography>
                            {lp.active && (
                              <Chip label='Ativa' color='success' size='small' />
                            )}
                          </Box>
                        }
                        secondary={`${lp.sections?.length ?? 0} seção(ões) • v${lp.version || '2.0'}`}
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < landpages.length - 1 && <Divider />}
                </Box>
              );
            })}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
